import axios, { AxiosError, AxiosHeaders } from "axios";
import { appConfig } from "@/shared/config";
import { extractServerCode, normalizeHttpError, restoreBlobErrorBody } from "./httpError";
import { API_TIMEOUT_MS, installRetry } from "./resilience";
import { installSimulatedLatency } from "./simulatedLatency";

export type SessionInvalidatedListener = () => void;

// Intake BE 가 Member 자격 상실을 알리는 403 코드. Overlord 토큰이 유효해도 이 앱의 세션은 끝난 것이라
// 401 과 같게 다룬다(docs/api.md). 그 밖의 403 은 개별 요청의 권한 문제다.
const MEMBERSHIP_REJECTED_CODES = new Set(["member.not_found", "member.inactive"]);

let apiJWT: string | null = null;
const sessionInvalidatedListeners = new Set<SessionInvalidatedListener>();

export const apiClient = axios.create({
  baseURL: appConfig.apiBaseUrl,
  timeout: API_TIMEOUT_MS,
  headers: {
    "Content-Type": "application/json",
    // 요청을 보낸 곳의 기록용 힌트다(권한이 아니다). BE 가 조사 사실 이력의 `client` 로 남긴다. MCP 게이트웨이는
    // `mcp` 를 보낸다. 다른 origin 으로 직접 붙을 때는 BE 의 CORS 허용 헤더에 이 이름이 있어야 한다.
    "X-Spnp-Client": "fe",
  },
  // 배열 쿼리 파라미터는 brackets 없이 키를 반복한다(`?k=a&k=b`). FastAPI 의 `list[...] = Query(...)`
  // 가 받는 형식이며, axios 기본값(`k[]=a`)은 그 alias 와 매칭되지 않는다. 스칼라 파라미터는 그대로다.
  paramsSerializer: { indexes: null },
});

installSimulatedLatency(apiClient);
// 재시도는 정규화/401 인터셉터보다 먼저 등록한다(원본 AxiosError를 먼저 보고 재발사).
installRetry(apiClient);

apiClient.interceptors.request.use((config) => {
  if (apiJWT === null) {
    return config;
  }

  const headers = AxiosHeaders.from(config.headers);
  headers.set("Authorization", `Bearer ${apiJWT}`);
  config.headers = headers;
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    // blob 요청(frame.png 등)의 에러 본문을 먼저 되돌려야 아래 정규화가 서버 code 를 읽는다.
    await restoreBlobErrorBody(error);
    // 지금 설치된 토큰이 거부된 경우에만 세션을 무효 처리한다. 토큰을 교체하면 교체 직전에 나간 요청이
    // 옛 토큰으로 401을 받아 뒤늦게 돌아오는데, 그것으로 방금 시작한 세션을 끊으면 안 된다.
    if (apiJWT !== null && isSessionRejection(error) && wasSentWithCurrentToken(error)) {
      apiJWT = null;
      notifySessionInvalidated();
    }
    return Promise.reject(normalizeHttpError(error));
  },
);

export function setJWT(jwt: string): void {
  apiJWT = jwt;
}

export function clearJWT(): void {
  apiJWT = null;
}

/**
 * 지금 세션의 Bearer 헤더. 세션이 없으면 null 이다.
 *
 * `apiClient` 는 이 헤더를 스스로 붙인다. 이 함수는 다른 client 로 나가는 요청 하나를 위해 있다 —
 * Overlord 의 연결 코드 발급은 Bearer 를 요구하고, 그 호출부가 헤더를 명시해 붙인다.
 */
export function sessionBearerHeader(): { Authorization: string } | null {
  return apiJWT === null ? null : { Authorization: `Bearer ${apiJWT}` };
}

/** 이전 세션의 늦은 스트림 응답이 새 세션을 종료하지 않게 한다. */
export function invalidateJWT(token: string): void {
  if (apiJWT !== token) return;
  apiJWT = null;
  notifySessionInvalidated();
}

export function onSessionInvalidated(listener: SessionInvalidatedListener): () => void {
  sessionInvalidatedListeners.add(listener);
  return () => {
    sessionInvalidatedListeners.delete(listener);
  };
}

function isSessionRejection(error: unknown): error is AxiosError {
  if (!(error instanceof AxiosError)) {
    return false;
  }

  const status = error.response?.status;
  if (status === 401) {
    return true;
  }

  return (
    status === 403 && MEMBERSHIP_REJECTED_CODES.has(extractServerCode(error.response?.data) ?? "")
  );
}

// 그 요청이 실제로 들고 나간 토큰이 지금 설치된 토큰인지. 재시도(resilience)는 재발사 때 요청
// 인터셉터를 다시 타므로 config 의 헤더는 마지막 시도가 보낸 값이다.
function wasSentWithCurrentToken(error: AxiosError): boolean {
  const sent = AxiosHeaders.from(error.config?.headers).get("Authorization");
  // 헤더를 못 읽으면(요청 인터셉터 이전에 실패) 판단 근거가 없으므로 현재 세션 문제로 본다.
  return typeof sent !== "string" || sent === `Bearer ${apiJWT}`;
}

function notifySessionInvalidated(): void {
  for (const listener of sessionInvalidatedListeners) {
    try {
      listener();
    } catch {
      // listener 오류로 다른 리스너의 호출을 막지 않는다.
    }
  }
}
